/* ══════════════════════════════════════════════════════════════════════════
 * HOME'S FOUR RAILS
 *
 * ⚠️ THE TITLES AND THE ORDER ARE THE LOCKED SCREEN'S, quoted from
 * `10_Home_LOCKED.html` — sec-hh, sec-dd, sec-lm, sec-gd, in that sequence,
 * with the headings exactly as they read there. Not "Daily Deal" singular,
 * not "Last-Minute" shortened. RULE 4: copy is never invented.
 *
 * ⚠️ AND THE KEYS ARE THE SCHEMA'S. `deal_type` is an enum declared in
 * migration 001 as ('happy_hour','daily','last_minute','game_day'), so a
 * fifth rail cannot be added here without the database gaining a fifth type.
 * The tab keys on the venue page (hh · daily · lm · gd) are the SCREEN's
 * names for the same four things and live in tabs.js; this maps between them
 * in one place so a card can open a venue at the right tab.
 *
 * ── WHY THIS IS NOT INSIDE Home.jsx ─────────────────────────────────────────
 *
 * The five View All pages are the same four lists, paged. Explore filters on
 * the same four. Zaryna answers "who has a Happy Hour tonight" from the same
 * four. Four copies of this table is the fault behind /v1, the sixteen
 * connector files and the two sign-in doors — so there is one.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ `count` IS EACH VIEW ALL SCREEN'S OWN SENTENCE, quoted from its locked
 * file — `view_all_happy_hour.html`, `view_all_daily_deals.html`,
 * `view_all_lms.html`, `view_all_gameday.html`. They are NOT one generic line.
 *
 * Al, 23 September 2026: *"underneath all, you've got places, but oh, 71
 * places."*  React printed `71 places` on every one of the four, because it
 * had one string. The locked screens say what the 71 ARE:
 *
 *   Happy Hour          48 spots with Happy Hour near you
 *   Daily Deals         61 daily deals near you
 *   Last-Minute         19 last-minute specials right now
 *   Game Day            23 Game Day venues near you
 *
 * ⚠️ AND THE SINGULAR IS WRITTEN OUT, not built by appending "s". "1 spots
 * with Happy Hour" and "1 last-minute specials" are both wrong, and the fix
 * for one of them is not the fix for the others.
 *
 * ⚠️⚠️ AND THE SINGULAR WAS BROKEN ANYWAY, BY THE CALLER. `ViewAll` passed
 * `countLabel(total)` — a STRING, because `Intl.NumberFormat().format` returns
 * one. `"1" === 1` is false, so a market with one venue read "1 spots with
 * Happy Hour near you". The comment above said this was fixed; it was fixed
 * HERE and undone one file away.
 *
 * So `count` now takes the NUMBER and does its own formatting. A function
 * that needs its argument in a particular shape, and trusts every caller to
 * remember, is a function that will be called wrongly. */
export const RAILS = [
  { type: 'happy_hour', title: 'Happy Hour', tab: 'hh',
    count: (n) => `${countLabel(n)} spot${n === 1 ? '' : 's'} with Happy Hour near you` },
  { type: 'daily', title: 'Daily Deals', tab: 'daily',
    count: (n) => `${countLabel(n)} daily deal${n === 1 ? '' : 's'} near you` },
  { type: 'last_minute', title: 'Last-Minute Specials', tab: 'lm',
    count: (n) => `${countLabel(n)} last-minute special${n === 1 ? '' : 's'} right now` },
  { type: 'game_day', title: 'Game Day', tab: 'gd',
    count: (n) => `${countLabel(n)} Game Day venue${n === 1 ? '' : 's'} near you` },
];

/* ⚠️ HOW MANY CARDS A RAIL HOLDS BEFORE "VIEW ALL".
 *
 * Al, 22 September: *"there's gonna be more than fifty… hundreds and hundreds
 * and hundreds of restaurants. If not, thousands. So we can't cap it at
 * fifty."*  He is right, and this is not that cap.
 *
 * The server pages: one request returns at most 50 rows, `offset` walks
 * through the rest with no ceiling, and every answer carries `total` — a real
 * count over every match. A market with four thousand venues reports four
 * thousand and pages through all of them.
 *
 * A RAIL is a different thing: a row somebody swipes sideways on a phone.
 * Twelve is what it holds before "View All (n)" takes over, and that number
 * is about the thumb, not the database. The count beside it is always the
 * real total, never twelve.
 */
export const RAIL_SIZE = 12;

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ "NEAR YOU" WAS A WORD ON A LABEL AND NOTHING ELSE.
 *
 * Every rail counts itself with Al's own copy — *"48 spots with Happy Hour
 * near you"* — and this function did not send a position. No `lat`, no `lng`,
 * no `radius_km`. So "near you" meant **anywhere in Hong Kong**, and somebody
 * standing in TST was offered a happy hour in Sai Kung under a line promising
 * it was near them.
 *
 * ⚠️ NOTHING THREW AND NOTHING LOGGED. The search route has taken
 * `lat,lng,radius_km` since it was written; the screens simply never passed
 * them, so the rails were correct queries answering a question nobody asked.
 *
 * Al, 24 September 2026: *"find the closest restaurants within a 500 meter
 * radius and then goes out."*
 *
 * ⚠️ AND WITHOUT A POSITION IT STILL WORKS. Somebody who refused the
 * permission, or who picked a city rather than being found, gets the whole
 * market — which is exactly what this did before, and is the right answer when
 * Eleat does not know where they are standing.
 * ══════════════════════════════════════════════════════════════════════════ */
export function railQuery(market, type, { limit = RAIL_SIZE, offset = 0, near = null, radiusKm = null, placement = false } = {}) {
  let q = `/discovery/search?market=${encodeURIComponent(market)}`
    + `&deal_type=${encodeURIComponent(type)}`
    + `&limit=${limit}&offset=${offset}`;
  /* ⚠️⚠️ ONLY HOME ASKS FOR PLACEMENT, AND IT HAS TO ASK.
     Al, 23 September 2026: *"the view all carousel is not a fucking paid. only
     the fucking premium placement on the homescreen is."*  And
     `Eleat_Home_Carousels_ViewAll_Premium_Placement.md` from the other side:
     *"No separate Featured block in View All — it is simply all venues.
     Premium placement lives on the home carousel, not here."*

     ⚠️ WHICH IS PRECISELY WHY IT IS A FLAG AND NOT SOMETHING THE SERVER WORKS
     OUT FROM `deal_type`. ViewAll.jsx calls this very function with the same
     four deal types; anything inferred would put a brand card on all five
     View All pages the day it shipped, and nobody would be looking. It
     defaults to false so a new caller is unpaid until somebody decides
     otherwise on purpose. */
  if (placement) q += '&placement=home';
  /* ⚠️ A RADIUS NEVER GOES WITHOUT A POINT — on its own it filters nothing. */
  /* ⚠️⚠️ THE POINT GOES EVEN WITH NO RADIUS — Al, 6 Oct 2026: "why are the
     restaurants on the homepage… all in fucking alphabetical order. they are
     to be by distance." Home asks the widest rung once (the whole market, no
     radius) and this line used to drop the point with the radius, so the
     server had nothing to measure from and fell back to A–Z (`ra.name`).
     A point with no radius filters nothing and orders nearest first. */
  if (near && Number.isFinite(near.lat) && Number.isFinite(near.lng)) {
    q += `&lat=${near.lat.toFixed(3)}&lng=${near.lng.toFixed(3)}`;
    if (radiusKm) q += `&radius_km=${radiusKm}`;
  }
  /* ⚠️ TO THE ~110 m GRID (3 decimals — the same cell where.service.js
       caches on). Al, 2 Oct 2026, "sluggish": a phone's fix differs in the
       5th decimal on every reading, so no two rail requests were ever the
       same and the server's short memory could never answer one. Cards still
       measure their km from the phone itself; this only orders the rail. */
  return q;
}

/* ⚠️ A NUMBER A PERSON READS, not a raw integer. "View All (1,284)" is the
   locked screen's shape; 1284 unseparated is not. Intl does the grouping the
   way the reader's own locale expects. */
export function countLabel(total) {
  if (typeof total !== 'number' || total < 0) return '';
  return new Intl.NumberFormat().format(total);
}
